import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthConfig, useLoginMock, useMe } from '../auth/useMe';
import LienzoMarca from '../components/LienzoMarca';
// `logo_blanco1.png` recortado a su contenido: el original trae el logo centrado en
// un lienzo de 800x600 con el 73% en aire, así que dimensionarlo por alto lo dejaba
// diminuto. Recortado pesa 4,9 kB —menos que el original— y se maqueta con un `h-*`.
import logoProcom from '../assets/logo-procom-blanco-h.png';

/**
 * Pantalla de ingreso. El fondo y la composición viven en `LienzoMarca`, que
 * comparte con `/sin-acceso`; acá va solo el contenido.
 *
 * Estructura: el logo como firma, el título a tamaño display en dos pesos, la línea
 * de marca, una instrucción y **una sola acción**. Sin tarjeta: el contenido se
 * apoya directo sobre el lienzo, corrido a la izquierda.
 */
export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get('next') ?? '/';

  const authConfig = useAuthConfig();
  const me = useMe();
  const loginMock = useLoginMock();
  /** Modo mock: la lista de usuarios de prueba arranca oculta (ver el bloque mock). */
  const [mostrarUsuarios, setMostrarUsuarios] = useState(false);

  // Ya autenticado → al destino. Va en un efecto: navegar durante el render es un
  // efecto secundario y React lo penaliza.
  useEffect(() => {
    if (me.data) navigate(next, { replace: true });
  }, [me.data, navigate, next]);
  if (me.data) return null;

  const config = authConfig.data;
  /**
   * El ingreso con Microsoft se puede apagar desde el servidor
   * (`LOGIN_MICROSOFT_HABILITADO=false`) mientras el registro de aplicaciones no
   * tenga el consentimiento del administrador del tenant. La pantalla no cambia:
   * el botón se dibuja igual pero deshabilitado, con el motivo abajo. Es mejor eso
   * que un botón que lleva a una pantalla de error de Microsoft.
   *
   * `!== false` y no `=== true`: un backend viejo no manda el campo, y ahí lo que
   * corresponde es dejarlo habilitado.
   */
  const microsoftHabilitado = config?.microsoftHabilitado !== false;
  /** Entrando por el botón principal del modo mock (no por una fila de la lista). */
  const entrandoDirecto =
    loginMock.isPending && loginMock.variables?.id === config?.mockUsuarioDefault;

  /** Clases del botón: mismas medidas y contorno, habilitado o no. */
  const claseBoton =
    'group mt-8 inline-flex min-h-12 items-center gap-3 rounded-xl border px-6 text-sm font-semibold transition duration-200 ease-out';

  return (
    <LienzoMarca>
      <img
        src={logoProcom}
        alt="Procom IT Solutions"
        width={628}
        height={163}
        className="h-10 w-auto sm:h-11 [@media(max-height:720px)]:h-9"
      />

      {/* Space Grotesk (`font-display`) con el tracking apenas cerrado: es una
          geométrica que ya viene bien espaciada, y apretarla como a una sans
          neutra le come las contraformas.
          Dos pesos: "Portal de" en 500 y en celeste hace de entrada, y "Compras"
          en 700 blanco es lo que queda grabado. El punto final —en celeste— es el
          remate que cierra el bloque; sin él la palabra queda al aire. */}
      <h1 className="mt-10 font-display text-[clamp(2.5rem,8.5vw,4.75rem)] font-bold leading-[0.92] tracking-[-0.03em] text-white [@media(max-height:720px)]:text-[3rem]">
        <span className="block font-medium text-accent-200/75">Portal de</span>
        Compras<span className="text-accent-400">.</span>
      </h1>

      {/* La línea de marca: el gesto que reemplaza al canto de la tarjeta. */}
      <span
        aria-hidden="true"
        className="mt-8 block h-[2px] w-28 rounded-full bg-[image:linear-gradient(90deg,#31b3e8_0%,#1f9e4a_60%,#b5cf2a_100%)]"
      />

      {!config && (
        <div aria-live="polite" className="mt-8 flex flex-col gap-4">
          <div className="h-4 w-60 animate-pulse rounded bg-white/10" />
          <div className="h-12 w-64 animate-pulse rounded-xl bg-white/10" />
        </div>
      )}

      {config?.mode === 'msal' && (
        <>
          <p className="mt-7 max-w-sm text-[0.95rem] leading-relaxed text-accent-200/85">
            Ingresá con tu cuenta de Procom.
          </p>

          {microsoftHabilitado ? (
            /* Contorno, y se queda contorno: el hover no lo pinta de color, solo
               sube el velo de blanco, aclara el borde, lo levanta un pelo y empuja
               la flecha. Sin capas superpuestas: la superficie clickeable del
               enlace es toda suya. */
            <a
              href={`/api/auth/login?next=${encodeURIComponent(next)}`}
              className={`${claseBoton} border-accent-300/45 bg-white/[0.05] text-white hover:-translate-y-0.5 hover:border-accent-300/75 hover:bg-white/[0.12] hover:shadow-[0_18px_32px_-18px_rgba(0,0,0,0.75)] active:translate-y-0 motion-reduce:transform-none`}
            >
              <LogoMicrosoft />
              Ingresar con Microsoft
              <i
                aria-hidden="true"
                className="ph ph-arrow-right text-base opacity-50 transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-100 motion-reduce:transition-none"
              />
            </a>
          ) : (
            /* Mismo botón, apagado. Va como <button disabled> y no como <a> sin
               href: así queda fuera del recorrido del teclado y los lectores de
               pantalla lo anuncian deshabilitado, en lugar de ofrecer un enlace
               que no lleva a ninguna parte. */
            <>
              <button
                type="button"
                disabled
                aria-describedby="motivo-microsoft"
                className={`${claseBoton} cursor-not-allowed border-white/15 bg-white/[0.03] text-white/45`}
              >
                <LogoMicrosoft apagado />
                Ingresar con Microsoft
                <i aria-hidden="true" className="ph ph-lock-simple text-base opacity-70" />
              </button>
              <p
                id="motivo-microsoft"
                className="mt-3.5 max-w-sm text-xs leading-relaxed text-amber-200/80"
              >
                {config.motivoMicrosoftDeshabilitado ??
                  'El ingreso con Microsoft está deshabilitado por configuración.'}
              </p>
            </>
          )}
        </>
      )}

      {config?.mode === 'mock' && (
        <>
          {/* Mismo copy y mismo botón que en producción: el ingreso simulado no
              tiene por qué verse distinto. El botón entra con `mockUsuarioDefault`
              (MOCK_USUARIO_DEFAULT en el .env) y la lista de usuarios de prueba
              queda detrás del enlace de abajo, para cuando haga falta cambiar de
              rol. */}
          <p className="mt-7 max-w-sm text-[0.95rem] leading-relaxed text-accent-200/85">
            Ingresá con tu cuenta de Procom.
          </p>

          <button
            type="button"
            disabled={loginMock.isPending || !config.mockUsuarioDefault}
            onClick={() =>
              config.mockUsuarioDefault &&
              loginMock.mutate({ id: config.mockUsuarioDefault, next })
            }
            className={`${claseBoton} border-accent-300/45 bg-white/[0.05] text-white hover:-translate-y-0.5 hover:border-accent-300/75 hover:bg-white/[0.12] hover:shadow-[0_18px_32px_-18px_rgba(0,0,0,0.75)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transform-none`}
          >
            <LogoMicrosoft />
            {entrandoDirecto ? 'Ingresando…' : 'Ingresar con Microsoft'}
            {entrandoDirecto ? (
              <span
                aria-hidden="true"
                className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
              />
            ) : (
              <i
                aria-hidden="true"
                className="ph ph-arrow-right text-base opacity-50 transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-100 motion-reduce:transition-none"
              />
            )}
          </button>

          {/* La única marca de que esto no es producción, y la puerta al selector
              de roles. Discreto a propósito. */}
          <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="text-accent-200/45">Ingreso simulado</span>
            <span aria-hidden="true" className="text-accent-200/25">
              ·
            </span>
            <button
              type="button"
              onClick={() => setMostrarUsuarios((v) => !v)}
              aria-expanded={mostrarUsuarios}
              className="text-accent-200/70 underline-offset-4 transition hover:text-accent-200 hover:underline"
            >
              {mostrarUsuarios ? 'Ocultar la lista' : 'Entrar con otro usuario'}
            </button>
          </div>

          {/* Y como la pantalla no scrollea, el desborde se resuelve acá adentro:
              con ocho usuarios mock no entra en un portátil de 768 px de alto. */}
          <ul
            hidden={!mostrarUsuarios}
            className="mt-4 max-h-[30vh] max-w-md divide-y divide-white/10 overflow-y-auto pr-1"
          >
            {config.usuariosMock?.map((u) => {
              const entrando = loginMock.isPending && loginMock.variables?.id === u.id;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    disabled={loginMock.isPending}
                    onClick={() => loginMock.mutate({ id: u.id, next })}
                    className="group flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-white">
                        {u.nombre}
                      </span>
                      <span className="block truncate text-xs text-accent-200/70">
                        {u.descripcion}
                      </span>
                    </span>
                    {entrando ? (
                      <span
                        aria-hidden="true"
                        className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/25 border-t-white"
                      />
                    ) : (
                      <i
                        aria-hidden="true"
                        className="ph ph-arrow-right shrink-0 text-white/30 transition group-hover:translate-x-0.5 group-hover:text-white"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {loginMock.isError && (
        <p
          role="alert"
          className="mt-5 max-w-md rounded-lg bg-red-500/15 px-3.5 py-2.5 text-xs leading-relaxed text-red-200"
        >
          No pudimos ingresar: {(loginMock.error as Error).message}
        </p>
      )}
    </LienzoMarca>
  );
}

/** El cuadrado de Microsoft. Apagado, va en escala de grises para no cantar color. */
function LogoMicrosoft({ apagado = false }: { apagado?: boolean }) {
  return (
    <span
      className={`grid h-5 w-5 place-items-center rounded-[3px] ${
        apagado ? 'bg-white/40 grayscale' : 'bg-white/95'
      }`}
    >
      <svg aria-hidden="true" width="13" height="13" viewBox="0 0 21 21">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
      </svg>
    </span>
  );
}
